import React, { useEffect, useRef, useCallback } from 'react';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACFRBy_sw8OtWEya';

// Track if script is loading/loaded globally
let scriptLoading = false;
let scriptLoaded = false;
const pendingCallbacks = [];

const loadTurnstileScript = (callback) => {
  if (scriptLoaded && window.turnstile) {
    callback();
    return;
  }

  pendingCallbacks.push(callback);

  if (scriptLoading) {
    return;
  }

  scriptLoading = true;
  const script = document.createElement('script');
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  script.async = true;
  script.defer = true;

  script.onload = () => {
    scriptLoaded = true;
    scriptLoading = false;
    pendingCallbacks.forEach(cb => cb());
    pendingCallbacks.length = 0;
  };

  document.head.appendChild(script);
};

const Turnstile = ({ onVerify, onError, onExpire }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onVerify, onError, onExpire });

  // Update callbacks ref when props change
  useEffect(() => {
    callbacksRef.current = { onVerify, onError, onExpire };
  }, [onVerify, onError, onExpire]);

  const renderWidget = useCallback(() => {
    if (containerRef.current && window.turnstile && widgetIdRef.current === null) {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => {
          if (callbacksRef.current.onVerify) callbacksRef.current.onVerify(token);
        },
        'error-callback': () => {
          if (callbacksRef.current.onError) callbacksRef.current.onError();
        },
        'expired-callback': () => {
          if (callbacksRef.current.onExpire) callbacksRef.current.onExpire();
        },
        theme: 'light',
        size: 'normal'
      });
    }
  }, []);

  useEffect(() => {
    loadTurnstileScript(() => {
      // Small delay to ensure DOM is ready
      setTimeout(renderWidget, 100);
    });

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Widget may already be removed
        }
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget]);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        justifyContent: 'center',
        margin: '15px 0'
      }}
    />
  );
};

export default Turnstile;
